/**
 * Wraps a page's content so a role that can open it but not change it sees it read-only:
 * a muted note, and every native input and button inside disabled (`<fieldset disabled>`,
 * which Radix triggers — plain buttons — respect). Every page is viewable by every role
 * (product decision 2026-10-08); this is how a page says "look, don't touch".
 *
 * Display only. The API still refuses the write, so removing this would show buttons that
 * 403, never grant anything.
 */

import { Eye, Lock } from "lucide-react";
import type { ReactNode } from "react";
import { useCan } from "@/hooks/use-can";
import type { Capability } from "@/lib/permissions";

export function ViewOnlyGate({
  capability,
  children,
}: {
  /** The capability this page's writes need. `null` for a page with nothing to change. */
  capability: Capability | null;
  children: ReactNode;
}) {
  const can = useCan(capability ?? "viewSolutionsDashboard");
  if (capability === null || can) return <>{children}</>;
  return (
    <div className="space-y-3">
      <p
        role="note"
        className="flex items-center gap-2 rounded-md border border-border bg-muted/40 px-3 py-2 text-sm text-muted-foreground"
      >
        <Eye className="h-4 w-4 shrink-0" aria-hidden />
        View only — your role can look at this but not change it.
      </p>
      <fieldset disabled className="contents">
        {children}
      </fieldset>
    </div>
  );
}

/**
 * For a page whose CONTENT, not just its controls, is protected (cost-derived figures, or
 * settings that decide who appears on a leadership slide). The page still opens.
 */
export function ProtectedContentNotice({ children }: { children: ReactNode }) {
  return (
    <div
      role="note"
      className="flex items-start gap-2.5 rounded-md border border-border bg-muted/40 px-4 py-3 text-sm text-muted-foreground"
    >
      <Lock className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
      <p>{children}</p>
    </div>
  );
}
